import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { approveAiDraft, getAiDrafts, rejectAiDraft } from "../../api/aiContent.service";

/**
 * AI drafts (cards + decks generated by AI, status 'draft', pending review).
 * Separate sub-state per content type: state.aiContent.decks / state.aiContent.cards
 */

export const getAiItemId = (item) => item?._id ?? item?.id;

const isOk = (resp) => resp && (resp.status === 200 || resp.status === 201) && resp.data !== undefined;

const errMessage = (resp) =>
  resp?.response?.data?.message || resp?.message || "Error fetching AI drafts";

// ---- thunks -----------------------------------------------------------------

export const fetchAiDrafts = createAsyncThunk(
  "aiContent/fetchAiDrafts",
  async ({ type, page, take, search } = {}, { rejectWithValue }) => {
    const resp = await getAiDrafts(type, { page, take, search });
    if (!isOk(resp)) return rejectWithValue({ type, message: errMessage(resp) });
    return { type, data: resp.data };
  }
);

export const approveAiContent = createAsyncThunk(
  "aiContent/approveAiContent",
  async ({ type, id }, { rejectWithValue }) => {
    const resp = await approveAiDraft(type, id);
    if (!isOk(resp)) return rejectWithValue({ type, id, message: errMessage(resp) });
    return { type, id };
  }
);

export const rejectAiContent = createAsyncThunk(
  "aiContent/rejectAiContent",
  async ({ type, id, reason, bugType }, { rejectWithValue }) => {
    const resp = await rejectAiDraft(type, id, reason, bugType);
    if (!isOk(resp)) return rejectWithValue({ type, id, message: errMessage(resp) });
    return { type, id };
  }
);

// ---- state ------------------------------------------------------------------

const emptyBucket = () => ({
  items: [],
  pagination: null,
  isLoading: false,
  error: null,
});

const initialState = {
  decks: emptyBucket(),
  cards: emptyBucket(),
  isActing: false, // an approve/reject request is in flight
};

// Map contract type ('deck'|'card') to state bucket key
const bucketKey = (type) => (type === "card" ? "cards" : "decks");

const removeItemFromBucket = (state, type, id) => {
  const bucket = state[bucketKey(type)];
  if (!bucket) return;
  bucket.items = (bucket.items || []).filter((item) => getAiItemId(item) !== id);
  if (bucket.pagination && typeof bucket.pagination.total === "number") {
    bucket.pagination = { ...bucket.pagination, total: Math.max(0, bucket.pagination.total - 1) };
  }
};

const fetchAiDraftsReducer = {
  [fetchAiDrafts.pending]: (state, action) => {
    const bucket = state[bucketKey(action.meta?.arg?.type)];
    if (!bucket) return;
    bucket.isLoading = true;
    bucket.error = null;
  },
  [fetchAiDrafts.fulfilled]: (state, action) => {
    const { type, data } = action.payload || {};
    const bucket = state[bucketKey(type)];
    if (!bucket) return;
    // Same shape as other admin list endpoints: { results, pagination }.
    // Be defensive: accept a plain array too.
    bucket.items = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
    bucket.pagination = data?.pagination ?? null;
    bucket.isLoading = false;
    bucket.error = null;
  },
  [fetchAiDrafts.rejected]: (state, action) => {
    const type = action.payload?.type ?? action.meta?.arg?.type;
    const bucket = state[bucketKey(type)];
    if (!bucket) return;
    bucket.isLoading = false;
    bucket.error = action.payload?.message || "error";
  },
};

const actReducerFor = (thunk) => ({
  [thunk.pending]: (state) => {
    state.isActing = true;
  },
  [thunk.fulfilled]: (state, action) => {
    state.isActing = false;
    const { type, id } = action.payload || {};
    removeItemFromBucket(state, type, id);
  },
  [thunk.rejected]: (state) => {
    state.isActing = false;
  },
});

export const aiContentSlice = createSlice({
  name: "aiContent",
  initialState,
  reducers: {},
  extraReducers: {
    ...fetchAiDraftsReducer,
    ...actReducerFor(approveAiContent),
    ...actReducerFor(rejectAiContent),
  },
});

export default aiContentSlice.reducer;
